import React, { useEffect, useRef, useState, useCallback, useMemo } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { SEO, generateCourseSchema } from '@alascorporation/seo'
import { MarkdownContent } from '@alascorporation/ui'
import { siteConfig } from '../config/seo.config'
import api from "../lib/api"
import { useAuthStore } from "../stores/authStore"
import { trackEvent } from "../lib/meta-pixel"
import { Badge } from "@/components/ui/badge"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import {
  ArrowLeft,
  ArrowRight,
  Lock,
  Clock,
  FileImage,
  Layers,
  Headphones,
  FileText,
  ExternalLink,
  BookOpenCheck,
  List as ListIcon,
  ShieldAlert,
  CheckCircle2,
  HelpCircle,
  Check,
  ClipboardList,
} from "lucide-react"
import { getProgramLabel } from "../config/schools"
import Footer from "@/components/layout/Footer"
import { PageSpinner } from "@/components/page"
import NetflixHlsPlayer from "@/components/video/NetflixHlsPlayer"
import YoutubeLessonPlayer from "@/components/video/YoutubeLessonPlayer"
import PdfViewer from "@/components/academy/PdfViewer"
import PurchaseCard from "@/components/academy/course-detail/PurchaseCard"
import InstructorSection from "@/components/academy/course-detail/InstructorSection"
import RelatedLearningPathsSection from "@/components/academy/course-detail/RelatedLearningPathsSection"
import LessonViewerHeader from "@/components/academy/course-detail/LessonViewerHeader"
import LessonPager from "@/components/academy/course-detail/LessonPager"
import SectionQuizView from "@/components/academy/course-detail/SectionQuizView"
import { getLessonTypeVisual } from "@/lib/academy/lesson-visuals"
import { AnimatedCheck } from "@/components/motion/AnimatedCheck"
import { useWompiCheckout } from "@/hooks/useWompiCheckout"
import { useMyCertificates } from "@/hooks/useMyCertificates"
import { useProgressHeartbeat } from "../hooks/useProgressHeartbeat"
import { useOptimisticLessonCompletion, type CourseProgressEntry } from "../hooks/useOptimisticLessonCompletion"
import { formatProgressPercent } from "@/lib/academy/format"
import { getDifficultyBadgeClass, getDifficultyLabel } from "@/lib/academy/difficulty"

const LOCKED_LESSON_REASON = "Disponible al aprobar la lección anterior"

interface Lesson {
  id: string
  title: string
  description: string
  duration_minutes: number
  order_index: number
  is_preview: boolean
  youtube_video_id: string
  is_private_video: boolean
  video_id: string | null
  video_status: "uploading" | "processing" | "ready" | "error" | null
  lesson_type?: 'video' | 'reading' | 'assignment'
  section_id?: string | null
  content?: string | null
}

interface Course {
  id: string
  title: string
  slug: string
  description: string
  short_description: string
  difficulty_level: string
  category: string
  estimated_hours: number
  instructor_name: string
  lessons: Lesson[]
  sections?: Array<{
    id: string
    title: string
    description?: string | null
    orderIndex: number
    lessons: Lesson[]
    section_quiz?: { id: string; title: string; passing_score: number } | null
  }>
  instructors?: Array<{
    id: string
    role: 'lead' | 'contributor'
    user: { id: string; name: string; avatarUrl?: string | null; bio?: string | null }
  }>
  what_you_will_learn?: string
  requirements?: string
  for_whom?: string
  price?: number | null
  isFree?: boolean
  prerequisites?: Array<{ id: string; slug: string; title: string }>
  programs?: Array<{
    program: { id: string; name: string; slug: string; color?: string | null; icon?: string | null }
  }>
}

type InstitutionalCourseRule = {
  course_id: string
  full_access: boolean
  lesson_ids: string[]
}

type EnrolledCourse = {
  id: string
  access_via_seedbed?: boolean
  access_via_program_level?: boolean
  completed_lessons?: number
  total_lessons?: number
  completed_lesson_ids?: string[]
  last_lesson?: { id: string; title: string } | null
}

type LessonResourceType = "video" | "podcast" | "slides" | "flashcards" | "infographic" | "document" | "link"

type LessonResource = {
  url: string
  type: LessonResourceType
  label: string
}

function getUniqueLinks(text?: string): string[] {
  if (!text) return []
  const links = text.match(/https?:\/\/[^\s)]+/g) ?? []
  return Array.from(new Set(links))
}

function detectResourceType(url: string): LessonResourceType {
  const lower = url.toLowerCase()
  if (lower.includes("youtube.com") || lower.includes("youtu.be") || lower.includes("vimeo.com")) return "video"
  if (
    lower.includes("spotify.com") ||
    lower.includes("soundcloud.com") ||
    lower.includes("podcasts.apple.com") ||
    lower.endsWith(".mp3")
  ) {
    return "podcast"
  }
  if (
    lower.includes("slides") ||
    lower.includes("docs.google.com/presentation") ||
    lower.includes("canva.com") ||
    lower.endsWith(".ppt") ||
    lower.endsWith(".pptx")
  ) {
    return "slides"
  }
  if (lower.includes("quizlet.com") || lower.includes("ankiweb.net") || lower.includes("flashcard")) return "flashcards"
  if (
    lower.endsWith(".png") ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".svg") ||
    lower.includes("infografia")
  ) {
    return "infographic"
  }
  if (lower.endsWith(".pdf") || lower.includes("drive.google.com/file")) return "document"
  return "link"
}

function labelForResourceType(type: LessonResourceType): string {
  if (type === "video") return "Video"
  if (type === "podcast") return "Podcast"
  if (type === "slides") return "Diapositivas"
  if (type === "flashcards") return "Flashcards"
  if (type === "infographic") return "Infografia"
  if (type === "document") return "Documento"
  return "Recurso"
}

function resourcesFromLesson(lesson: Lesson): LessonResource[] {
  const links = getUniqueLinks(lesson.description)
  const mapped = links.map((url) => {
    const type = detectResourceType(url)
    return { url, type, label: labelForResourceType(type) }
  })

  if (lesson.youtube_video_id) {
    mapped.unshift({
      url: `https://www.youtube.com/watch?v=${lesson.youtube_video_id}`,
      type: "video",
      label: "Video",
    })
  }

  const seen = new Set<string>()
  return mapped.filter((resource) => {
    const key = `${resource.type}:${resource.url}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function iconForResourceType(type: LessonResourceType) {
  if (type === "podcast") return Headphones
  if (type === "slides") return Layers
  if (type === "flashcards") return BookOpenCheck
  if (type === "infographic") return FileImage
  return FileText
}

function mainLessonType(lesson: Lesson): LessonResourceType {
  if (lesson.youtube_video_id) return "video"

  const combined = `${lesson.title} ${lesson.description || ""}`.toLowerCase()
  if (combined.includes("podcast") || combined.includes("audio")) return "podcast"
  if (combined.includes("diapositiva") || combined.includes("presentacion") || combined.includes("slides")) return "slides"
  if (combined.includes("flashcard") || combined.includes("quizlet") || combined.includes("anki")) return "flashcards"
  if (combined.includes("infografia") || combined.includes("infographic")) return "infographic"

  const links = getUniqueLinks(lesson.description)
  if (links.length > 0) {
    const linkType = detectResourceType(links[0])
    if (linkType !== "link") return linkType
  }

  return "document"
}

function isVideoLesson(lesson: Lesson): boolean {
  return Boolean(lesson.video_id || lesson.youtube_video_id) || lesson.lesson_type === 'video'
}

// Video y lectura (contenido en el DOM) son los únicos tipos con una señal de
// posición/scroll que vale la pena conservar para "reanudar donde quedé" —
// ya no determina si la lección puede completarse (eso pasa al abrirla,
// para cualquier tipo). Ver openspec/changes/academy-lesson-order-and-open-complete.
function supportsPositionBookkeeping(lesson: Lesson): boolean {
  return isVideoLesson(lesson) || (lesson.lesson_type === 'reading' && Boolean(lesson.content))
}

type SectionAccordionProps = {
  sectionIndex: number
  section: { id: string; title: string; orderIndex: number; lessons: Lesson[]; section_quiz?: { id: string; title: string; passing_score: number } | null }
  canAccessLesson: (lesson: Lesson, idx: number) => boolean
  completedLessonIds: Set<string>
  lessonToSlug: (title: string) => string
  slug: string
  navigate: (path: string) => void
  quizPassed?: (quizId: string) => boolean
  onSelectQuiz?: (quizId: string) => void
}

function SectionAccordion({ sectionIndex, section, canAccessLesson, completedLessonIds, lessonToSlug, slug, navigate, quizPassed, onSelectQuiz }: SectionAccordionProps) {
  const totalMinutes = section.lessons.reduce((sum, l) => sum + (l.duration_minutes || 0), 0)
  return (
    <div className="rounded-xl border border-foreground/10 overflow-hidden bg-foreground/[0.02]">
      <div className="px-4 py-4">
        <p className="text-sm font-semibold">
          {sectionIndex > 0 ? `${sectionIndex}. ` : ''}{section.title}
        </p>
        <p className="text-xs font-normal text-muted-foreground mt-0.5">
          {section.lessons.length} lección{section.lessons.length !== 1 ? 'es' : ''}
          {totalMinutes > 0 ? ` · ${totalMinutes} min` : ''}
        </p>
      </div>
      <div className="divide-y divide-foreground/5 border-t border-foreground/5">
        {section.lessons.map((lesson, idx) => {
          const accessible = canAccessLesson(lesson, idx)
          const isCompleted = completedLessonIds.has(lesson.id)
          return (
            <button key={lesson.id} type="button"
              onClick={() => accessible && navigate(`/courses/${slug}/${lessonToSlug(lesson.title)}`)}
              disabled={!accessible}
              aria-disabled={!accessible}
              title={accessible ? undefined : LOCKED_LESSON_REASON}
              className={`w-full text-left flex items-center gap-3 px-4 py-3 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${
                accessible ? 'hover:bg-foreground/5 cursor-pointer' : 'opacity-60 cursor-not-allowed'
              }`}>
              {(() => {
                const { icon: Icon, colorClass } = getLessonTypeVisual(lesson, { completed: isCompleted, accessible })
                return <Icon className={`w-4 h-4 shrink-0 ${colorClass} ${isCompleted ? 'motion-pop' : ''}`} aria-label={isCompleted ? "Lección vista" : undefined} />
              })()}
              <span className={`text-sm flex-1 ${isCompleted ? 'text-muted-foreground' : ''}`}>
                {lesson.title}
                {!accessible && <span className="block text-micro font-normal text-muted-foreground">{LOCKED_LESSON_REASON}</span>}
              </span>
              {lesson.duration_minutes ? (
                <span className="text-xs text-muted-foreground shrink-0 flex items-center gap-1">
                  <Clock className="w-3 h-3" />{lesson.duration_minutes}m
                </span>
              ) : null}
            </button>
          )
        })}
        {section.section_quiz && (
          <button type="button" onClick={() => onSelectQuiz?.(section.section_quiz!.id)}
            className="w-full text-left flex items-center gap-3 px-4 py-3 transition-colors outline-none hover:bg-foreground/5 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset cursor-pointer">
            <HelpCircle className="w-4 h-4 shrink-0 text-primary" />
            <span className="text-sm flex-1">{section.section_quiz.title}</span>
            {quizPassed?.(section.section_quiz.id) && (
              <span className="flex items-center gap-1 text-xs font-medium text-primary shrink-0">
                <CheckCircle2 className="w-3.5 h-3.5" /> Aprobado
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

type LessonNavItemProps = {
  lesson: Lesson
  accessible: boolean
  isActive: boolean
  isCompleted: boolean
  slug: string
  lessonToSlug: (title: string) => string
  navigate: (path: string) => void
}

function LessonNavItem({ lesson, accessible, isActive, isCompleted, slug, lessonToSlug, navigate }: LessonNavItemProps) {
  return (
    <button type="button"
      onClick={() => accessible && navigate(`/courses/${slug}/${lessonToSlug(lesson.title)}`)}
      disabled={!accessible}
      aria-current={isActive ? 'true' : undefined}
      title={accessible ? undefined : LOCKED_LESSON_REASON}
      className={`w-full text-left flex items-center gap-2 rounded-lg px-3 py-2 text-xs transition-colors duration-(--dur-fast) outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        isActive
          ? 'bg-primary/10 text-primary font-medium'
          : accessible
          ? 'hover:bg-foreground/5 text-foreground/80 cursor-pointer'
          : 'opacity-60 cursor-not-allowed text-foreground/70'
      }`}>
      <span className="shrink-0">
        {(() => {
          const { icon: Icon, colorClass } = getLessonTypeVisual(lesson, { completed: isCompleted, accessible })
          return <Icon className={`w-3.5 h-3.5 ${colorClass} ${isCompleted ? 'motion-pop' : ''}`} aria-label={isCompleted ? "Lección vista" : undefined} />
        })()}
      </span>
      <span className={`flex-1 truncate ${isCompleted && !isActive ? 'text-muted-foreground' : ''}`}>
        {lesson.title}
        {!accessible && <span className="sr-only"> — {LOCKED_LESSON_REASON}</span>}
      </span>
      {lesson.duration_minutes ? (
        <span className="text-muted-foreground shrink-0">{lesson.duration_minutes}m</span>
      ) : null}
    </button>
  )
}

function SectionQuizNavItem({ quiz, isActive, passed, isRequired, onSelect }: {
  quiz: { id: string; title: string }
  isActive: boolean
  passed?: boolean
  isRequired?: boolean
  onSelect: () => void
}) {
  return (
    <button type="button" onClick={onSelect}
      aria-current={isActive ? 'true' : undefined}
      className={`w-full text-left flex items-center gap-2 rounded-lg px-3 py-2 text-xs transition-colors duration-(--dur-fast) outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        isActive ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-foreground/5 text-foreground/80 cursor-pointer'
      }`}>
      <span className="shrink-0">
        <HelpCircle className="w-3.5 h-3.5 text-primary" />
      </span>
      <span className="flex-1 truncate">{quiz.title}</span>
      {isRequired === false && (
        <span className="shrink-0 rounded-full bg-foreground/10 px-1.5 py-0.5 text-micro font-semibold uppercase tracking-wide text-muted-foreground">
          Opcional
        </span>
      )}
      {passed && <CheckCircle2 className="w-3.5 h-3.5 shrink-0 text-primary" aria-label="Aprobado" />}
    </button>
  )
}

/**
 * Una lección se marca completada al abrirla (ver openspec/changes/
 * academy-lesson-order-and-open-complete) — no hay estado "parcial" que
 * mostrar, solo el checkmark una vez que la apertura se reportó al backend.
 */
function LessonProgressIndicator({ isAuthenticated, completed }: {
  isAuthenticated: boolean
  completed: boolean
}) {
  if (!isAuthenticated || !completed) return null

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/[0.02] p-6">
      <h4 className="text-xs font-bold text-primary uppercase mb-3 tracking-widest">Progreso</h4>
      <div className="motion-fade flex items-center justify-center gap-2 rounded-lg bg-primary/15 py-3 text-sm font-bold text-primary">
        <AnimatedCheck className="h-4 w-4" />
        Lección completada
      </div>
    </div>
  )
}

export default function CourseDetail() {
  const { slug, lessonSlug } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, user, sessionType, group } = useAuthStore()
  const [course, setCourse] = useState<Course | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null)
  const [accessReady, setAccessReady] = useState(false)
  const [hasGeneralAccess, setHasGeneralAccess] = useState(false)
  const [activeSectionQuizId, setActiveSectionQuizId] = useState<string | null>(null)
  const [institutionalRule, setInstitutionalRule] = useState<{
    fullAccess: boolean
    allowedLessonIds: Set<string>
  } | null>(null)
  const [activeLesson, setActiveLesson] = useState<Lesson | null>(null)
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [revealedFlashcards, setRevealedFlashcards] = useState<Record<number, boolean>>({})
  const [buyLoading, setBuyLoading] = useState(false)
  const [buyError, setBuyError] = useState<string | null>(null)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [courseProgress, setCourseProgress] = useState<{ completed: number; total: number; lastLessonTitle?: string } | null>(null)
  const [completedLessonIds, setCompletedLessonIds] = useState<Set<string>>(new Set())
  const [quizPassedStatus, setQuizPassedStatus] = useState<Record<string, boolean>>({})
  const [quizIsRequired, setQuizIsRequired] = useState<Record<string, boolean>>({})
  const [heroScrolled, setHeroScrolled] = useState(false)

  // Hysteresis (entra en 64px, sale en 24px) para que la barra compacta no
  // parpadee cuando el scroll se queda oscilando justo en el umbral.
  const handleMainScroll = (e: React.UIEvent<HTMLElement>) => {
    const top = e.currentTarget.scrollTop
    setHeroScrolled((prev) => (prev ? top > 24 : top > 64))
  }

  const lessonResources = useMemo(() => (activeLesson ? resourcesFromLesson(activeLesson) : []), [activeLesson])
  const additionalResources = useMemo(
    () => lessonResources.filter((resource) => resource.type !== "video"),
    [lessonResources],
  )
  const activeLessonType = useMemo(
    () => (activeLesson ? mainLessonType(activeLesson) : "document"),
    [activeLesson],
  )
  // Lección cuyo medio principal es video (propio o YouTube): usa el layout "ajustado al viewport".
  const usesVideoLayout = Boolean(
    activeLesson && (activeLesson.video_id || (activeLessonType === "video" && activeLesson.youtube_video_id)),
  )
  const pdfResource = useMemo(
    () => (activeLessonType === "document" ? lessonResources.find((r) => r.type === "document") : undefined),
    [activeLessonType, lessonResources],
  )

  const [resumePosition, setResumePosition] = useState(0)

  // Modo cine (solo escritorio): el reproductor usa todo el ancho disponible.
  const [theater, setTheater] = useState<boolean>(() => {
    try { return localStorage.getItem('academy:player:theater') === '1' } catch { return false }
  })
  const toggleTheater = useCallback(() => {
    setTheater((v) => {
      const next = !v
      try { localStorage.setItem('academy:player:theater', next ? '1' : '0') } catch { /* sin storage: no persiste */ }
      return next
    })
  }, [])

  // Autoplay solo cuando el estudiante llega a la lección con "Siguiente"/"Anterior"
  // (o por la cuenta atrás del reproductor). Una carga directa por URL no reproduce sola.
  const [autoPlayLessonId, setAutoPlayLessonId] = useState<string | null>(null)

  // El contenedor que hace scroll en la vista de lección es <main>, no window.
  const lessonMainRef = useRef<HTMLElement | null>(null)
  const scrollLessonToTop = useCallback(() => {
    const main = lessonMainRef.current
    if (!main || main.scrollTop <= 0) return
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    main.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' })
  }, [])

  // Video: solo cuenta como "activo" el intervalo si hubo timeupdate real del
  // reproductor (no cuenta un seek puntual ni tiempo con el video en pausa).
  const videoActiveSinceTickRef = useRef(false)
  const videoPositionRef = useRef(0)
  const videoDurationRef = useRef(0)
  // Lectura: máximo histórico de scroll del contenedor de la lección.
  const readScrollPercentRef = useRef(0)
  // Duración real de YouTube ya conocida en este montaje (capturada por
  // YoutubeLessonPlayer), reenviada en cada heartbeat hasta que el backend
  // la persista (write-once, idempotente reenviarla).
  const youtubeDurationRef = useRef<number | undefined>(undefined)
  const lessonDoneRef = useRef(false)
  const [lessonDone, setLessonDone] = useState(false)

  // Aplica de inmediato (mismo frame, antes de que resuelva la red) el efecto
  // visual de "lección completada": ícono ✓ + contador de curso incrementado.
  // Ver openspec/changes/academy-optimistic-lesson-progress/design.md (D1).
  const applyLessonCompletedOptimistically = useCallback((lessonId: string) => {
    setCourseProgress((prev) => (prev ? { ...prev, completed: Math.min(prev.total, prev.completed + 1) } : prev))
    setCompletedLessonIds((prev) => (prev.has(lessonId) ? prev : new Set(prev).add(lessonId)))
    if (activeLesson?.id === lessonId) {
      lessonDoneRef.current = true
      setLessonDone(true)
    }
  }, [activeLesson?.id])

  // Reconcilia el contador optimista con el agregado real que ya viaja en la
  // respuesta del PUT (sin round-trip adicional a /courses/enrolled/me).
  const reconcileLessonCompletion = useCallback((_lessonId: string, courses: CourseProgressEntry[]) => {
    if (!course?.id) return
    const match = courses.find((entry) => entry.course_id === course?.id)
    if (match) {
      setCourseProgress((prev) => (prev ? { ...prev, completed: match.completed_lessons, total: match.total_lessons } : prev))
      return
    }
    // Fallback de compatibilidad: si el backend todavía no envía `courses`
    // (rollout en progreso), reconciliamos con el GET anterior.
    if (courses.length > 0) return
    api.get('/academy/courses/enrolled/me')
      .then(({ data }) => {
        const enrolledCourses = data.data as EnrolledCourse[]
        const enrolledCourse = enrolledCourses.find((c) => c.id === course?.id)
        if (enrolledCourse && enrolledCourse.total_lessons) {
          setCourseProgress({
            completed: enrolledCourse.completed_lessons ?? 0,
            total: enrolledCourse.total_lessons,
            lastLessonTitle: enrolledCourse.last_lesson?.title,
          })
          setCompletedLessonIds(new Set(enrolledCourse.completed_lesson_ids ?? []))
        }
      })
      .catch(() => {})
  }, [course?.id])

  // Revierte el estado optimista si el servidor nunca confirmó la completitud
  // (fallo persistente tras el reintento automático del hook).
  const revertLessonCompletion = useCallback((lessonId: string) => {
    setCourseProgress((prev) => (prev ? { ...prev, completed: Math.max(0, prev.completed - 1) } : prev))
    setCompletedLessonIds((prev) => {
      if (!prev.has(lessonId)) return prev
      const next = new Set(prev)
      next.delete(lessonId)
      return next
    })
    if (activeLesson?.id === lessonId) {
      lessonDoneRef.current = false
      setLessonDone(false)
    }
  }, [activeLesson?.id])

  const { markCompleted } = useOptimisticLessonCompletion({
    onOptimistic: applyLessonCompletedOptimistically,
    onConfirmed: reconcileLessonCompletion,
    onRollback: revertLessonCompletion,
  })

  // Ya no se completa una lección solo por abrirla — ver
  // openspec/changes/academy-lesson-watch-time-completion (reemplaza
  // academy-lesson-order-and-open-complete). Este efecto solo recupera el
  // estado de progreso existente (posición de reanudación, scroll máximo,
  // si ya estaba completada). La completitud real llega por: umbral de
  // watch-time/scroll del heartbeat, onEnded del reproductor, o — solo para
  // YouTube — la señal de apertura del player (ver handleYoutubeOpened).
  useEffect(() => {
    videoActiveSinceTickRef.current = false
    videoPositionRef.current = 0
    videoDurationRef.current = 0
    readScrollPercentRef.current = 0
    youtubeDurationRef.current = undefined
    lessonDoneRef.current = false
    setLessonDone(false)

    if (!activeLesson?.id || !isAuthenticated) { setResumePosition(0); return }
    api.get(`/academy/lessons/${activeLesson.id}/progress`)
      .then(({ data }) => {
        const progress = data.data as { completed?: boolean; last_position_seconds?: number; max_scroll_percent?: number | null } | null
        setResumePosition(progress?.last_position_seconds ?? 0)
        readScrollPercentRef.current = progress?.max_scroll_percent ?? 0

        if (progress?.completed) {
          lessonDoneRef.current = true
          setLessonDone(true)
        }
      })
      .catch(() => setResumePosition(0))
  }, [activeLesson?.id, isAuthenticated])

  const handleVideoTimeUpdate = useCallback((currentTime: number, duration: number) => {
    if (duration <= 0) return
    videoActiveSinceTickRef.current = true
    videoPositionRef.current = currentTime
    videoDurationRef.current = duration
  }, [])

  const handleVideoEnded = useCallback(() => {
    videoActiveSinceTickRef.current = true
    videoPositionRef.current = videoDurationRef.current
    // El fin de reproducción completa la lección incluso si no se cruzó el
    // 80% de watch-time acumulado (OR, ver design.md D6).
    const lessonId = activeLesson?.id
    if (lessonId) markCompleted(lessonId)
  }, [activeLesson?.id, markCompleted])

  // Señal de "lección de YouTube abierta": dispara un reporte de progreso
  // inmediato (no espera al próximo heartbeat de 15s) que — según la
  // duración conocida o no — deja que el backend evalúe el umbral del 80%
  // o aplique el fallback transitorio mientras la duración no se ha
  // capturado todavía. Ver design.md D3.
  const handleYoutubeOpened = useCallback((durationSecondsIfKnown: number | null) => {
    const lessonId = activeLesson?.id
    if (!lessonId) return
    if (durationSecondsIfKnown) youtubeDurationRef.current = durationSecondsIfKnown

    const payload: Record<string, number> = { delta_seconds: 0 }
    if (durationSecondsIfKnown) payload.youtube_duration_seconds = durationSecondsIfKnown

    api.put(`/academy/lessons/${lessonId}/progress`, payload)
      .then(({ data }) => {
        const result = data?.data as { completed?: boolean; courses?: CourseProgressEntry[] } | undefined
        if (result?.completed) {
          applyLessonCompletedOptimistically(lessonId)
          reconcileLessonCompletion(lessonId, result.courses ?? [])
        }
      })
      .catch(() => {})
  }, [activeLesson?.id, applyLessonCompletedOptimistically, reconcileLessonCompletion])

  const isActiveSinceLastTick = useCallback(() => {
    if (!activeLesson) return false
    if (isVideoLesson(activeLesson)) {
      const wasActive = videoActiveSinceTickRef.current
      videoActiveSinceTickRef.current = false
      return wasActive
    }
    return true
  }, [activeLesson])

  const getPositionSeconds = useCallback(
    () => (activeLesson && isVideoLesson(activeLesson) ? videoPositionRef.current : undefined),
    [activeLesson],
  )
  const getScrollPercent = useCallback(
    () => (activeLesson && !isVideoLesson(activeLesson) ? readScrollPercentRef.current : undefined),
    [activeLesson],
  )

  // Toda lección se completa al abrirla, así que el indicador de progreso
  // aplica a cualquier tipo — solo depende de estar autenticado.
  const showProgressSidebar = isAuthenticated && Boolean(activeLesson)

  // El heartbeat ya no decide si la lección se completó (eso pasa al abrirla);
  // sigue activo solo para reanudar posición de video / scroll de lectura.
  const supportsHeartbeatBookkeeping = Boolean(
    activeLesson && (supportsPositionBookkeeping(activeLesson) || Boolean(pdfResource)),
  )

  const getYoutubeDurationSeconds = useCallback(
    () => (activeLesson?.youtube_video_id ? youtubeDurationRef.current : undefined),
    [activeLesson?.youtube_video_id],
  )

  useProgressHeartbeat({
    lessonId: activeLesson?.id,
    enabled: Boolean(isAuthenticated && supportsHeartbeatBookkeeping),
    isActiveSinceLastTick,
    getPositionSeconds,
    getScrollPercent,
    getYoutubeDurationSeconds,
  })

  const updateMaxScrollPercent = useCallback((percent: number) => {
    readScrollPercentRef.current = Math.max(readScrollPercentRef.current, percent)
  }, [])

  const handleLessonMainScroll = useCallback((e: React.UIEvent<HTMLElement>) => {
    const el = e.currentTarget
    const scrollable = el.scrollHeight - el.clientHeight
    const percent = scrollable > 0 ? Math.min(100, Math.max(0, (el.scrollTop / scrollable) * 100)) : 100
    updateMaxScrollPercent(percent)
  }, [updateMaxScrollPercent])

  const isLessonView = Boolean(lessonSlug)

  const lessonToSlug = (title: string) =>
    title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")

  const canAccessLesson = useCallback(
    (lesson: Lesson, index: number) => {
      if (hasGeneralAccess) return true
      if (institutionalRule) {
        if (institutionalRule.fullAccess) return true
        return institutionalRule.allowedLessonIds.has(lesson.id)
      }
      return lesson.is_preview || index === 0
    },
    [hasGeneralAccess, institutionalRule],
  )

  const loadCourse = useCallback(async () => {
    setAccessReady(false)
    setHasGeneralAccess(false)
    setInstitutionalRule(null)
    setLoadErrorMessage(null)

    let courseData: Course
    try {
      const { data } = await api.get(`/academy/courses/${slug}`)
      courseData = data.data
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      setLoadErrorMessage(
        status === 404
          ? 'Este curso no existe o fue movido.'
          : 'No pudimos cargar este curso. Verifica tu conexión e intenta de nuevo.',
      )
      setAccessReady(true)
      setLoading(false)
      return
    }

    try {
      setCourse(courseData)
      trackEvent('ViewContent', {
        content_name: courseData.title,
        content_ids: [courseData.id],
        content_type: 'product',
      })


      if (isAuthenticated) {
        // Auto-enroll in free courses so progress tracking works immediately.
        // Group sessions never enroll via this endpoint (server-side 403 by
        // design) — their Enrollment row is created on first progress write
        // instead (lessons.ts writeProgressForOwner), so skip the call.
        if (courseData.isFree && sessionType !== 'group') {
          api.post(`/academy/courses/${courseData.id}/enroll`, {}).catch(() => {})
        }

        try {
          const enrolled = await api.get("/academy/courses/enrolled/me")
          const enrolledCourses = enrolled.data.data
          const enrolledCourse = (enrolledCourses as EnrolledCourse[]).find((c) => c.id === courseData.id)
          setHasGeneralAccess(Boolean(enrolledCourse && (!enrolledCourse.access_via_seedbed || enrolledCourse.access_via_program_level)))
          if (enrolledCourse && enrolledCourse.total_lessons) {
            setCourseProgress({
              completed: enrolledCourse.completed_lessons ?? 0,
              total: enrolledCourse.total_lessons,
              lastLessonTitle: enrolledCourse.last_lesson?.title,
            })
            setCompletedLessonIds(new Set(enrolledCourse.completed_lesson_ids ?? []))
          } else {
            setCourseProgress(null)
            setCompletedLessonIds(new Set())
          }
        } catch {
          // Optional enrollment lookup can fail without blocking course view.
        }

        try {
          const seedbedsAccess = await api.get("/academy/seedbeds/me")
          const rules = (seedbedsAccess.data?.data?.course_rules ?? []) as InstitutionalCourseRule[]
          const rule = rules.find((item) => item.course_id === courseData.id)
          setInstitutionalRule(
            rule
              ? {
                  fullAccess: rule.full_access,
                  allowedLessonIds: new Set(rule.lesson_ids),
                }
              : null,
          )
        } catch {
          setInstitutionalRule(null)
        }

        const hasAnySectionQuiz = (courseData.sections ?? []).some((s) => s.section_quiz)
        if (hasAnySectionQuiz) {
          try {
            const statusRes = await api.get(`/academy/section-quizzes/course/${courseData.id}/status`)
            setQuizPassedStatus(statusRes.data.data.quizzes ?? {})
            setQuizIsRequired(statusRes.data.data.is_required ?? {})
          } catch {
            setQuizPassedStatus({})
            setQuizIsRequired({})
          }
        } else {
          setQuizPassedStatus({})
          setQuizIsRequired({})
        }
      }
    } catch (err) {
      console.error('Unexpected error rendering course:', err)
      setLoadErrorMessage('No pudimos cargar este curso. Verifica tu conexión e intenta de nuevo.')
    } finally {
      setAccessReady(true)
      setLoading(false)
    }
  }, [slug, isAuthenticated, sessionType])

  useEffect(() => {
    loadCourse()
  }, [loadCourse])

  useEffect(() => {
    if (!course) return
    if (!isLessonView) {
      setActiveLesson(null)
      return
    }
    if (!accessReady) return

    const bySlugIndex = lessonSlug
      ? course.lessons.findIndex((l) => lessonToSlug(l.title) === lessonSlug)
      : -1

    if (bySlugIndex >= 0 && canAccessLesson(course.lessons[bySlugIndex], bySlugIndex)) {
      setActiveLesson(course.lessons[bySlugIndex])
      return
    }

    const firstAvailableIndex = course.lessons.findIndex((lesson, idx) => canAccessLesson(lesson, idx))
    if (firstAvailableIndex >= 0) {
      const fallbackLesson = course.lessons[firstAvailableIndex]
      setActiveLesson(fallbackLesson)
      navigate(`/courses/${course.slug}/${lessonToSlug(fallbackLesson.title)}`, { replace: true })
    } else {
      navigate(`/courses/${course.slug}`, { replace: true })
    }
  }, [course, lessonSlug, isLessonView, navigate, canAccessLesson, accessReady])

  const handleLessonClick = (lesson: Lesson, index: number) => {
    if (!course || !canAccessLesson(lesson, index)) return
    navigate(`/courses/${course.slug}/${lessonToSlug(lesson.title)}`)
    scrollLessonToTop()
  }

  // Búsqueda del siguiente item accesible en el orden plano de course.lessons,
  // independiente de si el curso usa secciones o no.
  const nextAccessibleLesson = useMemo(() => {
    if (!course || !activeLesson) return null
    const currentIndex = course.lessons.findIndex((l) => l.id === activeLesson.id)
    if (currentIndex === -1) return null
    for (let i = currentIndex + 1; i < course.lessons.length; i++) {
      if (canAccessLesson(course.lessons[i], i)) return course.lessons[i]
    }
    return null
  }, [course, activeLesson, canAccessLesson])

  // Lección anterior accesible (mismo criterio de acceso que la siguiente).
  const prevAccessibleLesson = useMemo(() => {
    if (!course || !activeLesson) return null
    const currentIndex = course.lessons.findIndex((l) => l.id === activeLesson.id)
    if (currentIndex <= 0) return null
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (canAccessLesson(course.lessons[i], i)) return course.lessons[i]
    }
    return null
  }, [course, activeLesson, canAccessLesson])

  // Posición en el orden plano y si la siguiente existe pero está bloqueada
  // (para explicarlo en el botón en lugar de ocultarlo).
  const lessonPosition = useMemo(() => {
    if (!course || !activeLesson) return null
    const index = course.lessons.findIndex((l) => l.id === activeLesson.id)
    if (index === -1) return null
    return {
      current: index + 1,
      total: course.lessons.length,
      nextBlocked: index < course.lessons.length - 1 && !nextAccessibleLesson,
    }
  }, [course, activeLesson, nextAccessibleLesson])

  // Exámenes obligatorios (isRequired) del curso que el estudiante aún no ha
  // aprobado — con lecciones al 100% son lo único que falta para que
  // Enrollment pase a 'completed' y se pueda descargar el certificado. Ver
  // openspec/changes/academy-required-exams-block-completion.
  const pendingRequiredQuizzes = useMemo(() => {
    if (!course?.sections) return []
    return course.sections
      .map((s) => s.section_quiz)
      .filter((q): q is NonNullable<typeof q> => Boolean(q))
      .filter((q) => quizIsRequired[q.id] !== false && quizPassedStatus[q.id] !== true)
  }, [course, quizIsRequired, quizPassedStatus])

  const courseComplete = Boolean(
    courseProgress && courseProgress.total > 0 && courseProgress.completed >= courseProgress.total && pendingRequiredQuizzes.length === 0,
  )

  // Certificado del curso (si ya fue emitido). Solo se consulta al completar el curso.
  const { find: findCertificate, refetch: refetchCertificates, refreshing: refreshingCertificates, pollExhausted: certificatePollExhausted } = useMyCertificates(
    courseComplete && isAuthenticated,
    { pollWhileNone: course ? { id: course.id, slug: course.slug, title: course.title } : undefined },
  )
  const certificate = course
    ? findCertificate({ id: course.id, slug: course.slug, title: course.title })
    : ({ status: 'loading' } as const)

  const activeSectionTitle = useMemo(() => {
    if (!course?.sections || !activeLesson) return null
    return course.sections.find((s) => s.lessons.some((l) => l.id === activeLesson.id))?.title ?? null
  }, [course, activeLesson])

  const handleNavigateNextLesson = useCallback(() => {
    if (!course || !nextAccessibleLesson) return
    setAutoPlayLessonId(nextAccessibleLesson.id)
    navigate(`/courses/${course.slug}/${lessonToSlug(nextAccessibleLesson.title)}`)
    scrollLessonToTop()
  }, [course, nextAccessibleLesson, navigate, scrollLessonToTop])

  const handleNavigatePreviousLesson = useCallback(() => {
    if (!course || !prevAccessibleLesson) return
    setAutoPlayLessonId(prevAccessibleLesson.id)
    navigate(`/courses/${course.slug}/${lessonToSlug(prevAccessibleLesson.title)}`)
    scrollLessonToTop()
  }, [course, prevAccessibleLesson, navigate, scrollLessonToTop])

  // Título del documento: lección · curso (útil en pestañas, historial y lector de pantalla).
  useEffect(() => {
    if (!course || !activeLesson || !isLessonView) return
    const previous = document.title
    document.title = `${activeLesson.title} · ${course.title} · SateLab Academy`
    return () => { document.title = previous }
  }, [course, activeLesson, isLessonView])

  const { startCheckout } = useWompiCheckout(() => setBuyLoading(false))

  const handleBuy = async () => {
    if (!course) return
    setBuyError(null)
    trackEvent('InitiateCheckout', {
      content_name: course.title,
      content_ids: [course.id],
      currency: 'COP',
      value: course.price ?? 0,
    })
    setBuyLoading(true)
    const error = await startCheckout('/academy/payments/create', { course_id: course.id })
    if (error) {
      setBuyError(error)
      setBuyLoading(false)
    }
  }

  const slideDeck = useMemo(() => {
    if (!activeLesson) return []
    const lines = activeLesson.description
      ? activeLesson.description.split(/[.!?]\s+/).map((item) => item.trim()).filter(Boolean)
      : []
    const bullets = lines.length > 0 ? lines : ["Objetivo", "Concepto", "Aplicación"]
    return [
      { title: "Contexto", points: bullets.slice(0, 3) },
      { title: "Desarrollo", points: bullets.slice(1, 4).length > 0 ? bullets.slice(1, 4) : bullets.slice(0, 3) },
      { title: "Cierre", points: ["Resumen", "Pasos a seguir"] },
    ]
  }, [activeLesson])

  const flashcards = useMemo(() => {
    if (!activeLesson) return []
    const lines = activeLesson.description
      ? activeLesson.description.split(/[.!?]\s+/).map((item) => item.trim()).filter(Boolean)
      : []
    return [
      { front: "Objetivo", back: activeLesson.title },
      { front: "Concepto Clave", back: lines[0] || "Contenido de la lección" },
      { front: "Aplicación", back: lines[1] || "Práctica recomendada" },
      { front: "Autoevaluación", back: "Explica esto en tus palabras" },
    ]
  }, [activeLesson])

  useEffect(() => {
    setActiveSlideIndex(0)
    setRevealedFlashcards({})
  }, [activeLesson?.id])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <PageSpinner label="Cargando curso…" />
      </div>
    )
  }

  if (loadErrorMessage || !course) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background text-center">
        <p className="text-lg font-bold text-foreground">{loadErrorMessage || 'No pudimos cargar este curso.'}</p>
        <div className="flex gap-3">
          <button
            onClick={() => loadCourse()}
            className="rounded-lg border border-border bg-foreground/5 px-6 py-2.5 text-sm font-bold text-foreground outline-none transition-colors hover:bg-foreground/10 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            Reintentar
          </button>
          <Link
            to="/courses"
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground outline-none transition-opacity hover:opacity-90 focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            Ver catálogo
          </Link>
        </div>
      </div>
    )
  }

  if (activeSectionQuizId) {
    return (
      <div
        className="fixed inset-0 z-50 overflow-y-auto bg-background"
        onKeyDown={(e) => { if (e.key === 'Escape') setActiveSectionQuizId(null) }}
      >
        <button
          type="button"
          onClick={() => setActiveSectionQuizId(null)}
          className="fixed right-4 top-4 z-10 inline-flex min-h-11 items-center gap-2 rounded-lg border border-border bg-background px-4 text-sm font-medium text-foreground outline-none transition-colors hover:bg-foreground/5 focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Salir del examen
        </button>
        <SectionQuizView
          sectionQuizId={activeSectionQuizId}
          onExit={() => setActiveSectionQuizId(null)}
          hasNextLesson={Boolean(nextAccessibleLesson)}
          onNavigateNext={() => { setActiveSectionQuizId(null); handleNavigateNextLesson() }}
        />
      </div>
    )
  }

  // Helper check for user access greeting
  const userGreeting = sessionType === 'group' && group
    ? `Hola, ${group.name}`
    : user ? `Hola, ${user.name}` : "Hola"

  /**
   * Lista de navegación de lecciones — compartida entre el sidebar de escritorio
   * (renderSidebarLeft) y el drawer de mobile/tablet (renderMobileLessonNav).
   */
  const renderLessonNavContent = () => (
    <div className="space-y-1">
      <h3 className="text-xs uppercase tracking-widest text-muted-foreground">CONTENIDO</h3>
      <div className="mt-4 space-y-1">
        {course.sections && course.sections.length > 0 ? (
          course.sections.map((section, sIdx) => (
            <div key={section.id} className="mb-3">
              <p className="text-micro font-bold uppercase tracking-widest text-muted-foreground px-3 py-1.5">
                {sIdx + 1}. {section.title}
              </p>
              {section.lessons.map((lesson, idx) => {
                const accessible = canAccessLesson(lesson, idx)
                const isActive = activeLesson?.id === lesson.id
                return (
                  <LessonNavItem key={lesson.id} lesson={lesson} accessible={accessible}
                    isActive={isActive} isCompleted={completedLessonIds.has(lesson.id)}
                    slug={slug!} lessonToSlug={lessonToSlug} navigate={navigate} />
                )
              })}
              {section.section_quiz && (
                <SectionQuizNavItem quiz={section.section_quiz}
                  isActive={activeSectionQuizId === section.section_quiz.id}
                  passed={quizPassedStatus[section.section_quiz.id] === true}
                  isRequired={quizIsRequired[section.section_quiz.id]}
                  onSelect={() => setActiveSectionQuizId(section.section_quiz!.id)} />
              )}
            </div>
          ))
        ) : (
          course.lessons.map((lesson, idx) => {
            const accessible = canAccessLesson(lesson, idx)
            const isActive = activeLesson?.id === lesson.id
            return (
              <LessonNavItem key={lesson.id} lesson={lesson} accessible={accessible}
                isActive={isActive} isCompleted={completedLessonIds.has(lesson.id)}
                slug={slug!} lessonToSlug={lessonToSlug} navigate={navigate} />
            )
          })
        )}
      </div>
    </div>
  )

  /**
   * ESTILO DASHBOARD: BARRA LATERAL IZQUIERDA (solo escritorio, >=1024px)
   */
  const renderSidebarLeft = () => (
    <div className="hidden w-72 shrink-0 border-r border-border bg-background lg:flex">
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto scroll-thin p-6">
          <Link to="/courses" className="group mb-8 flex items-center gap-2 rounded-md text-sm font-medium text-muted-foreground outline-none transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-ring">
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0" />
            Volver a Cursos
          </Link>

          <div className="mb-6">
            <p className="text-xl font-bold text-foreground tracking-tight">{userGreeting}</p>
            <p className="text-xs text-muted-foreground mt-1">¿Listo para continuar?</p>
          </div>

          {renderLessonNavContent()}
        </div>
        <div className="shrink-0 p-6">
          {courseProgress ? (
            <div className="rounded-lg bg-primary/5 p-4 border border-primary/10">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-foreground">Tu avance</p>
                <p className="text-xs font-semibold text-primary">
                  {formatProgressPercent(courseProgress.completed, courseProgress.total)}%
                </p>
              </div>
              <div role="progressbar" aria-label="Avance del curso" aria-valuemin={0} aria-valuemax={100} aria-valuenow={formatProgressPercent(courseProgress.completed, courseProgress.total) ?? 0} className="platzi-progress mt-2">
                <div
                  className="h-full w-full rounded-full bg-primary motion-progress motion-progress-in"
                  style={{ transform: `scaleX(${(formatProgressPercent(courseProgress.completed, courseProgress.total) ?? 0) / 100})` }}
                />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {courseProgress.completed}/{courseProgress.total} lecciones completadas
              </p>
            </div>
          ) : (
            <div className="rounded-lg bg-primary/5 p-4 border border-primary/10">
              <p className="text-xs font-semibold text-primary">Tip del día</p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                Toma notas durante las lecciones para mejorar la retención de conceptos aeroespaciales.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  /**
   * Drawer de lecciones para mobile/tablet (<1024px) — reemplaza el vacío que
   * dejaba renderSidebarLeft() al estar oculto con "hidden ... lg:block".
   */
  const renderMobileLessonNav = () => (
    <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
      <SheetTrigger className="flex min-h-11 items-center gap-2 rounded-lg border border-border bg-foreground/5 px-4 py-2 text-xs font-bold text-foreground outline-none transition-colors hover:bg-foreground/10 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 lg:hidden">
        <ListIcon className="h-4 w-4" />
        Temario del curso
      </SheetTrigger>
      <SheetContent side="left" className="w-4/5">
        <SheetHeader>
          <SheetTitle>{course.title}</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto scroll-thin px-4 pb-4" onClickCapture={() => setMobileNavOpen(false)}>
          {renderLessonNavContent()}
        </div>
      </SheetContent>
    </Sheet>
  )

  /**
   * Barra superior mínima (<1024px): reemplaza al navbar público, que duplicaba
   * la navegación de la app. En escritorio el enlace de volver vive en el sidebar.
   */
  const renderMobileTopBar = () => (
    <div className="flex shrink-0 items-center gap-3 border-b border-border bg-background px-4 py-1 lg:hidden">
      <Link to={isAuthenticated ? "/dashboard" : "/courses"} className="inline-flex min-h-11 items-center gap-2 rounded-md text-sm font-medium text-muted-foreground outline-none transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-ring">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {isAuthenticated ? "Mi aprendizaje" : "Cursos"}
      </Link>
      <span className="min-w-0 flex-1 truncate text-right text-xs text-muted-foreground">{course.title}</span>
    </div>
  )

  /** Avance del curso visible también en móvil (el sidebar solo existe en escritorio). */
  const renderMobileProgress = () => courseProgress ? (
    <div className="mb-4 rounded-lg border border-border bg-card p-3 lg:hidden">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-foreground">Tu avance</span>
        <span className="font-semibold text-primary">
          {courseProgress.completed}/{courseProgress.total} · {formatProgressPercent(courseProgress.completed, courseProgress.total)}%
        </span>
      </div>
      <div role="progressbar" aria-label="Avance del curso" aria-valuemin={0} aria-valuemax={100} aria-valuenow={formatProgressPercent(courseProgress.completed, courseProgress.total) ?? 0} className="platzi-progress mt-2">
        <div
          className="h-full w-full rounded-full bg-primary motion-progress motion-progress-in"
          style={{ transform: `scaleX(${(formatProgressPercent(courseProgress.completed, courseProgress.total) ?? 0) / 100})` }}
        />
      </div>
    </div>
  ) : null

  /**
   * ESTILO DASHBOARD: BARRA LATERAL DERECHA (Información del Curso)
   */
  const showBuyButton = accessReady && !hasGeneralAccess && !institutionalRule && course && !course.isFree && Boolean(course.price)

  const renderSidebarRight = () => (
    <div className="hidden w-80 shrink-0 border-l border-border bg-background xl:flex">
      <div className="h-full min-h-0 w-full overflow-y-auto scroll-thin p-8 space-y-8">

        <PurchaseCard
          price={course.price}
          showBuyButton={Boolean(showBuyButton)}
          isAuthenticated={isAuthenticated}
          buyLoading={buyLoading}
          buyError={buyError}
          onBuy={handleBuy}
        />

        {(course.requirements || (course.prerequisites && course.prerequisites.length > 0)) && (
          <div>
            <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary mb-3">
              <ShieldAlert className="h-3.5 w-3.5" />
              Requisitos
            </h3>
            <ul className="space-y-2.5">
              {course.prerequisites?.map((p) => (
                <li key={p.id} className="flex items-start gap-2 text-sm leading-relaxed">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                  <Link to={`/courses/${p.slug}`} className="rounded text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring">
                    {p.title}
                  </Link>
                </li>
              ))}
              {course.requirements?.split('\n').filter(Boolean).map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground/85 leading-relaxed">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="border-t border-border pt-6">
          <h3 className="text-sm font-semibold text-foreground mb-1">¿Necesitas ayuda?</h3>
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">
            Nuestro equipo puede resolver tus dudas técnicas.
          </p>
          <a
            href={`mailto:info@satelab.org?subject=${encodeURIComponent(`Ayuda con el curso ${course.slug}`)}`}
            className="inline-flex min-h-11 items-center gap-1 rounded text-xs font-semibold text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
          >
            Contactar soporte
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </a>
        </div>
      </div>
    </div>
  )

  /**
   * VISTA DE LECCIÓN ACTIVA (VIEWER)
   */
  if (isLessonView && activeLesson) {
    return (
      <div className="flex h-screen flex-col overflow-hidden bg-background">
        {renderMobileTopBar()}

        <div className="flex min-h-0 flex-1 overflow-hidden">
          {renderSidebarLeft()}

          <main ref={lessonMainRef} className="min-h-0 flex-1 overflow-y-auto scroll-thin" onScroll={handleLessonMainScroll}>
          <div
            className={`mx-auto w-full px-4 py-3 sm:px-6 lg:py-5 ${usesVideoLayout && theater ? "lg:max-w-none" : "max-w-5xl"}`}
            // Alto reservado fuera del video en escritorio: padding + cabecera + barra anterior/siguiente.
            style={{ "--lesson-chrome": "10rem" } as React.CSSProperties}
          >
            <div className="mb-2 lg:hidden">
              {renderMobileLessonNav()}
            </div>
            <LessonViewerHeader
              courseTitle={course.title}
              sectionTitle={activeSectionTitle}
              lessonTitle={activeLesson.title}
              lessonType={activeLesson.lesson_type}
              orderIndex={activeLesson.order_index}
              durationMinutes={activeLesson.duration_minutes}
              theater={theater}
              onToggleTheater={usesVideoLayout ? toggleTheater : undefined}
            />

            {/* Contenedor de medios. En móvil, el video se queda fijo arriba mientras se lee. */}
            <div
              className={
                usesVideoLayout
                  ? "sticky top-0 z-30 -mx-4 bg-background sm:-mx-6 lg:static lg:mx-0 landscape:max-lg:static"
                  : "relative"
              }
            >
            <div
              className={
                usesVideoLayout
                  ? "mx-auto w-full overflow-hidden bg-black lg:max-w-[min(100%,calc((100dvh_-_var(--lesson-chrome))*16/9))] lg:rounded-xl"
                  : "mb-10 overflow-hidden rounded-xl border border-border bg-card p-1"
              }
            >
              <div className={`overflow-hidden bg-black ${usesVideoLayout ? "lg:rounded-xl" : "rounded-xl"}`}>
                {activeLesson.video_id && activeLesson.video_status === "ready" ? (
                  <NetflixHlsPlayer
                    key={activeLesson.video_id}
                    videoId={activeLesson.video_id}
                    title={activeLesson.title}
                    initialTime={resumePosition}
                    onTimeUpdate={(ct, dur) => handleVideoTimeUpdate(ct, dur)}
                    onEnded={handleVideoEnded}
                    nextLesson={nextAccessibleLesson ? { title: nextAccessibleLesson.title, onNext: handleNavigateNextLesson } : null}
                    onPrevious={prevAccessibleLesson ? handleNavigatePreviousLesson : undefined}
                    theater={theater}
                    onToggleTheater={toggleTheater}
                    autoPlay={autoPlayLessonId === activeLesson.id}
                  />
                ) : activeLesson.video_id && activeLesson.video_status !== "ready" ? (
                  <div className="flex aspect-video w-full items-center justify-center bg-black px-6 text-center text-slate-200">
                    <div role="status" className="max-w-sm">
                      {activeLesson.video_status === "error" ? (
                        <>
                          <p className="text-base font-semibold">Hubo un problema con este video</p>
                          <p className="mt-1 text-sm text-slate-300">
                            Escríbenos y lo resolvemos lo antes posible.
                          </p>
                          <a
                            href={`mailto:info@satelab.org?subject=${encodeURIComponent(`Problema con el video: ${activeLesson.title}`)}`}
                            className="mt-4 inline-flex min-h-11 items-center rounded-lg border border-white/20 px-4 text-sm font-semibold text-white outline-none transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            Escribir a soporte
                          </a>
                        </>
                      ) : (
                        <>
                          <p className="text-base font-semibold">
                            {activeLesson.video_status === "uploading" || activeLesson.video_status === "processing"
                              ? "Estamos procesando este video"
                              : "Este video aún no está disponible"}
                          </p>
                          <p className="mt-1 text-sm text-slate-300">Vuelve en unos minutos.</p>
                          <button
                            type="button"
                            onClick={() => loadCourse()}
                            className="mt-4 inline-flex min-h-11 items-center rounded-lg border border-white/20 px-4 text-sm font-semibold text-white outline-none transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            Actualizar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ) : activeLessonType === "video" && activeLesson.youtube_video_id ? (
                  <div className="aspect-video w-full relative">
                    {activeLesson.is_private_video && (
                      <div className="absolute inset-x-4 top-4 z-10">
                        <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-black/90 p-4">
                          <Lock className="h-5 w-5 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
                          <div>
                            <p className="text-xs font-bold text-white mb-1">Video privado</p>
                            <p className="text-xs text-white/85 leading-relaxed">
                              Para verlo, inicia sesión en YouTube con el correo que te autorizó tu institución.
                              Si dice "Video no disponible", abre YouTube, inicia sesión y vuelve aquí.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    <YoutubeLessonPlayer
                      key={activeLesson.youtube_video_id}
                      videoId={activeLesson.youtube_video_id}
                      title={activeLesson.title}
                      initialTime={resumePosition}
                      onTimeUpdate={(ct, dur) => handleVideoTimeUpdate(ct, dur)}
                      onEnded={handleVideoEnded}
                      onOpened={handleYoutubeOpened}
                    />
                  </div>
                ) : activeLesson.lesson_type === "reading" && activeLesson.content ? (
                  <div className="px-2 py-6">
                    <div className="mx-auto max-w-[70ch] rounded-lg border border-border bg-card p-6">
                      <div className="whitespace-pre-wrap font-sans text-base text-foreground/90 leading-relaxed">
                        {activeLesson.content}
                      </div>
                    </div>
                  </div>
                ) : activeLessonType === "slides" ? (
                  <div className="aspect-video w-full flex flex-col justify-center bg-secondary px-6 sm:px-12 py-12">
                     <div className="mb-6">
                        <p className="text-micro uppercase tracking-widest text-primary font-bold">Diapositiva {activeSlideIndex + 1} de {slideDeck.length}</p>
                        <h3 className="text-3xl sm:text-4xl font-extrabold text-foreground mt-2 leading-none">{slideDeck[activeSlideIndex]?.title}</h3>
                     </div>
                     <ul className="space-y-3 mb-8">
                        {slideDeck[activeSlideIndex]?.points.map((p, idx) => (
                           <li key={idx} className="flex items-start gap-4 text-slate-200">
                             <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary" />
                             <span className="text-sm sm:text-lg font-medium tracking-tight leading-tight">{p}</span>
                           </li>
                        ))}
                     </ul>
                     <div className="flex items-center gap-4 mt-auto">
                        <button onClick={() => setActiveSlideIndex(v => Math.max(0, v - 1))} disabled={activeSlideIndex === 0} aria-label="Diapositiva anterior"
                                className="h-11 w-11 border border-foreground/10 rounded-xl flex items-center justify-center text-foreground outline-none transition-colors hover:bg-foreground/5 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40">
                           <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                        </button>
                        <div role="progressbar" aria-label="Avance de diapositivas" aria-valuemin={1} aria-valuemax={slideDeck.length} aria-valuenow={activeSlideIndex + 1} className="flex-1 h-1 bg-foreground/10 rounded-full overflow-hidden">
                           <div className="motion-progress motion-progress-in h-full w-full bg-primary" style={{ transform: `scaleX(${(activeSlideIndex + 1) / slideDeck.length})` }} />
                        </div>
                        <button onClick={() => setActiveSlideIndex(v => Math.min(slideDeck.length - 1, v + 1))} disabled={activeSlideIndex >= slideDeck.length - 1} aria-label="Diapositiva siguiente"
                                className="h-11 w-11 border border-foreground/10 rounded-xl flex items-center justify-center text-foreground outline-none transition-colors hover:bg-foreground/5 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40">
                           <ArrowRight className="h-5 w-5" aria-hidden="true" />
                        </button>
                     </div>
                  </div>
                ) : activeLessonType === "flashcards" ? (
                  <div className="aspect-video w-full grid grid-cols-2 gap-4 bg-black/40 p-4">
                     {flashcards.map((card, idx) => (
                        <div key={idx} className={`flashcard-container h-full w-full ${revealedFlashcards[idx] ? "flashcard-flipped" : ""}`}>
                           <button onClick={() => setRevealedFlashcards(v => ({...v, [idx]: !v[idx]}))}
                                   className="flashcard-inner w-full h-full rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring">
                              {/* FRONT */}
                              <div className="flashcard-front bg-sidebar border border-border rounded-xl p-6 group">
                                 <p className="text-micro uppercase text-primary font-black tracking-widest mb-4">Tarjeta {idx + 1}</p>
                                 <p className="text-base sm:text-xl font-bold text-foreground leading-tight">{card.front}</p>
                                 <div className="mt-auto px-5 py-2 rounded-lg bg-foreground/5 text-micro font-black text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                                    REVELAR RESPUESTA
                                 </div>
                              </div>
                              {/* BACK */}
                              <div className="flashcard-back bg-primary border border-black/10 rounded-xl p-6">
                                 <p className="text-micro uppercase text-primary-foreground font-black tracking-widest mb-4">Respuesta</p>
                                 <p className="text-base sm:text-xl font-extrabold text-primary-foreground leading-tight">{card.back}</p>
                                 <div className="mt-auto px-5 py-2 rounded-lg bg-black/10 text-micro font-black text-primary-foreground">
                                    Cerrar carta
                                 </div>
                              </div>
                           </button>
                        </div>
                     ))}
                  </div>
                ) : pdfResource ? (
                  <PdfViewer url={pdfResource.url} onScrollPercentChange={updateMaxScrollPercent} />
                ) : (
                  <div className="flex aspect-video items-center justify-center bg-secondary p-12 text-center text-foreground">
                     <div>
                        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-xl bg-primary/10 border border-primary/20 text-primary">
                           {React.createElement(iconForResourceType(activeLessonType), { className: "h-10 w-10" })}
                        </div>
                        <h3 className="text-2xl font-bold mb-2">Recurso de {labelForResourceType(activeLessonType)}</h3>
                        <p className="text-muted-foreground max-w-sm mx-auto">Esta lección se desarrolla mediante material interactivo adjunto.</p>
                     </div>
                  </div>
                )}
              </div>
            </div>
            </div>

            {lessonPosition && (
              <div className={usesVideoLayout ? "mb-6" : "-mt-6 mb-8"}>
                <LessonPager
                  current={lessonPosition.current}
                  total={lessonPosition.total}
                  hasPrevious={Boolean(prevAccessibleLesson)}
                  hasNext={Boolean(nextAccessibleLesson)}
                  nextBlocked={lessonPosition.nextBlocked}
                  onPrevious={handleNavigatePreviousLesson}
                  onNext={handleNavigateNextLesson}
                />
              </div>
            )}

            <div className="mb-6">{renderMobileProgress()}</div>

            {/* Grid de Contenido — sin sidebar de progreso, la descripción ocupa todo el ancho */}
            <div className={`grid gap-8 ${showProgressSidebar ? "lg:grid-cols-3" : ""}`}>
              <div className={`space-y-8 ${showProgressSidebar ? "lg:col-span-2" : ""}`}>
                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Resumen y Objetivos</h2>
                  <div className="max-w-none w-full text-muted-foreground leading-relaxed">
                    {activeLesson.description ? (
                      <MarkdownContent content={activeLesson.description} />
                    ) : (
                      "En esta lección cubriremos los fundamentos necesarios para avanzar en el curso."
                    )}
                  </div>
                </section>

                {additionalResources.length > 0 && (
                  <section>
                    <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                       <Layers className="h-5 w-5 text-primary" /> Recursos de Aprendizaje
                    </h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {additionalResources.map((res) => {
                        const Icon = iconForResourceType(res.type)
                        return (
                          <a key={res.url} href={res.url} target="_blank" rel="noreferrer"
                             className="group flex flex-col justify-between rounded-lg border border-border bg-foreground/[0.02] p-5 outline-none transition-colors hover:border-primary/30 hover:bg-foreground/[0.05] focus-visible:ring-2 focus-visible:ring-ring">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-foreground/5 group-hover:bg-primary/10 transition-colors">
                                <Icon className="h-5 w-5 text-muted-foreground group-hover:text-primary" />
                              </div>
                              <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            <div>
                              <p className="font-bold text-foreground text-sm mb-1">{res.label}</p>
                              <p className="text-micro text-muted-foreground truncate">{res.url}</p>
                            </div>
                          </a>
                        )
                      })}
                    </div>
                  </section>
                )}
              </div>

              {showProgressSidebar && (
                <div className="space-y-6">
                  <LessonProgressIndicator isAuthenticated={isAuthenticated} completed={lessonDone} />
                </div>
              )}
            </div>
          </div>

          <Footer />
          </main>

          {renderSidebarRight()}
        </div>
      </div>
    )
  }

  /**
   * VISTA DE DASHBOARD DE CURSO (LANDING DENTRO DEL CURSO)
   */
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background text-foreground">
      {course && (
        <SEO
          config={siteConfig}
          title={course.title}
          description={course.short_description || course.description?.slice(0, 160) || siteConfig.defaultDescription}
          path={`/courses/${course.slug}`}
          type="course"
          author={course.instructors?.[0] ? { name: course.instructors[0].user.name } : undefined}
          schemas={[generateCourseSchema(siteConfig, {
            name: course.title,
            description: course.description,
            duration: course.estimated_hours ? `PT${course.estimated_hours}H` : undefined,
            level: course.difficulty_level,
            path: `/courses/${course.slug}`,
            instructor: course.instructors?.[0] ? { name: course.instructors[0].user.name } : undefined,
          })]}
        />
      )}
      {renderMobileTopBar()}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {renderSidebarLeft()}

        <main className="min-h-0 flex-1 overflow-y-auto scroll-thin antialiased" onScroll={handleMainScroll}>
        <div className="container mx-auto max-w-5xl px-6 pt-6 lg:hidden">
          {renderMobileLessonNav()}
        </div>

        {/*
          Barra de título+descripción, fija arriba del área de scroll. Sin animación:
          cambia directamente entre la versión completa y la compacta al cruzar el
          umbral de scroll. line-clamp trunca con "…" en vez de desbordar.
        */}
        {heroScrolled ? (
          <div className="sticky top-0 z-20 border-b border-border bg-background">
            <div className="container mx-auto max-w-5xl px-6 py-3">
              <h1 className="line-clamp-1 text-base font-semibold tracking-tight text-foreground">
                {course.title}
              </h1>
              <p className="line-clamp-1 text-xs text-muted-foreground">
                {course.short_description || course.description}
              </p>
            </div>
          </div>
        ) : (
          <div className="border-b border-transparent bg-background">
            <div className="container mx-auto max-w-5xl px-6 pt-6 pb-2">
              <h1 className="line-clamp-2 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                {course.title}
              </h1>
              <p className="mt-2 line-clamp-2 max-w-3xl text-sm text-muted-foreground leading-relaxed sm:text-base">
                {course.short_description || course.description}
              </p>
            </div>
          </div>
        )}

        {/* HERO SECTION DASHBOARD */}
        <section className="relative overflow-hidden pt-4 pb-10">
          <div className="container relative mx-auto max-w-5xl px-6">
            {/* Una sola línea: nivel, programa, duración, lecciones e instructor — sin bloques sueltos */}
            <div className="mb-6 flex flex-wrap items-center gap-x-5 gap-y-3">
              <Badge className={`border py-1 px-4 text-xs font-bold uppercase tracking-wider ${getDifficultyBadgeClass(course.difficulty_level)}`}>
                {getDifficultyLabel(course.difficulty_level) ?? course.difficulty_level}
              </Badge>
              {course.category && (
                <Badge variant="outline" className="border-foreground/10 text-muted-foreground py-1 px-4">
                  {getProgramLabel(course.category)}
                </Badge>
              )}
              <div className="h-4 w-px bg-foreground/10" />
              {course.estimated_hours ? (
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4 text-primary" />
                  {course.estimated_hours}h de contenido
                </span>
              ) : null}
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Layers className="h-4 w-4 text-primary" />
                {course.lessons.length} lecciones
              </span>
              {course.instructors && course.instructors.length > 0 ? (
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  {course.instructors[0].user.avatarUrl ? (
                    <img src={course.instructors[0].user.avatarUrl} alt={course.instructors[0].user.name}
                      className="h-6 w-6 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/20 text-micro font-bold text-primary">
                      {course.instructors[0].user.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  {course.instructors[0].user.name}
                </span>
              ) : course.instructor_name ? (
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/20 text-micro font-bold text-primary">
                    {course.instructor_name.charAt(0).toUpperCase()}
                  </span>
                  {course.instructor_name}
                </span>
              ) : null}
            </div>

            {/* La info de progreso es la prioridad #1 para un estudiante inscrito: siempre con acción, no solo dato */}
            {courseProgress ? (
              <div className="flex max-w-2xl items-center gap-5 rounded-xl border border-primary/20 bg-primary/[0.04] p-5">
                <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                  {formatProgressPercent(courseProgress.completed, courseProgress.total)}%
                </div>
                <div className="min-w-0 flex-1">
                  <div role="progressbar" aria-label="Avance del curso" aria-valuemin={0} aria-valuemax={100} aria-valuenow={formatProgressPercent(courseProgress.completed, courseProgress.total) ?? 0} className="platzi-progress">
                    <div
                      className="h-full w-full rounded-full bg-primary motion-progress motion-progress-in"
                      style={{ transform: `scaleX(${(formatProgressPercent(courseProgress.completed, courseProgress.total) ?? 0) / 100})` }}
                    />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {courseProgress.completed}/{courseProgress.total} lecciones completadas
                  </p>
                </div>
                {courseProgress.lastLessonTitle && (
                  <button
                    onClick={() => navigate(`/courses/${course.slug}/${lessonToSlug(courseProgress.lastLessonTitle!)}`)}
                    className="flex shrink-0 items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground outline-none transition-[filter,transform] hover:brightness-110 motion-safe:active:scale-[0.98] focus-visible:ring-3 focus-visible:ring-ring/50"
                  >
                    Continuar
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            ) : showBuyButton ? null : hasGeneralAccess ? (
              <button
                onClick={() => {
                  const first = course.lessons.find((l, idx) => canAccessLesson(l, idx))
                  if (first) navigate(`/courses/${course.slug}/${lessonToSlug(first.title)}`)
                }}
                className="motion-press motion-link-arrow flex w-fit items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground outline-none transition-[filter,transform] hover:brightness-110 focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                Empezar curso
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : null}

            {accessReady && courseComplete && (
              <div className="motion-rise mt-4 flex max-w-2xl flex-col gap-3 rounded-xl border border-primary/30 bg-primary/[0.06] p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <AnimatedCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <p role="status" aria-live="polite" className="text-sm text-foreground">
                    <span className="font-semibold">Completaste este curso.</span>{' '}
                    {certificate.status === 'found' && 'Tu certificado ya está disponible.'}
                    {certificate.status === 'none' && !certificatePollExhausted && 'Estamos emitiendo tu certificado. Puede tardar unos minutos.'}
                    {certificate.status === 'none' && certificatePollExhausted && 'Aún no aparece tu certificado. Pulsa «Actualizar» o escríbenos y lo revisamos.'}
                    {(certificate.status === 'ambiguous' || certificate.status === 'error') && 'No pudimos ubicar tu certificado automáticamente. Encuéntralo en tu panel, en la lista de certificados.'}
                  </p>
                </div>
                {certificate.status === 'none' && (
                  <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => { void refetchCertificates() }}
                      disabled={refreshingCertificates}
                      aria-busy={refreshingCertificates}
                      className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg border border-border bg-background px-5 text-sm font-semibold text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-60"
                    >
                      {refreshingCertificates && (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none" aria-hidden="true" />
                      )}
                      Actualizar
                    </button>
                    {certificatePollExhausted && (
                      <a
                        href={`mailto:info@satelab.org?subject=${encodeURIComponent(`Certificado pendiente: ${course?.title ?? ''}`)}`}
                        className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-lg bg-primary px-5 text-sm font-semibold text-primary-foreground outline-none transition-[filter] hover:brightness-110 focus-visible:ring-3 focus-visible:ring-ring/50"
                      >
                        Escribir a soporte
                      </a>
                    )}
                  </div>
                )}
                {(certificate.status === 'found' || certificate.status === 'ambiguous' || certificate.status === 'error') && (
                  <Link
                    to={certificate.status === 'found' ? `/certificates/${certificate.code}` : '/certificados'}
                    className="motion-press motion-link-arrow inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg bg-primary px-5 text-sm font-semibold text-primary-foreground outline-none transition-[filter,transform] hover:brightness-110 focus-visible:ring-3 focus-visible:ring-ring/50"
                  >
                    {certificate.status === 'found' ? 'Ver certificado' : 'Ver mis certificados'}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                )}
              </div>
            )}

            {accessReady && courseProgress && courseProgress.completed >= courseProgress.total && pendingRequiredQuizzes.length > 0 && (
              <div className="motion-fade mt-4 flex max-w-2xl items-start gap-3 rounded-xl border border-border bg-muted p-4">
                <ClipboardList className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <p className="text-sm text-foreground">
                  Completaste todas las lecciones. Aprueba {pendingRequiredQuizzes.length === 1 ? 'el examen obligatorio pendiente' : `los ${pendingRequiredQuizzes.length} exámenes obligatorios pendientes`} para poder descargar tu certificado.{' '}
                  <Link to="/ayuda" className="font-semibold text-primary underline-offset-4 hover:underline">Ayuda</Link>
                </p>
              </div>
            )}
          </div>
        </section>

        {/* CONTENIDO DEL CURSO - TIMELINE/LISTA */}
        <section className="pb-24">
          <div className="container mx-auto max-w-5xl px-6">
            <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
              <div>
                <h2 className="text-3xl font-semibold text-foreground tracking-tight mb-2">Contenido del curso</h2>
                <p className="text-muted-foreground">Sigue el hilo conductor diseñado por expertos aeroespaciales.</p>
              </div>
              <div className="flex h-12 items-center gap-4 rounded-full border border-border bg-foreground/[0.02] px-6">
                <span className="text-sm font-semibold text-foreground">{course.lessons.length} módulos</span>
                <div className="h-4 w-[1px] bg-foreground/10" />
                <span className="text-sm font-semibold text-primary">100% online</span>
              </div>
            </div>

            {/* Info del curso: un solo panel con secciones internas, no cards repetidas. Los requisitos viven en la columna derecha (renderSidebarRight). */}
            {(course.what_you_will_learn || course.for_whom) && (
              <div className="mt-8 mb-10 grid grid-cols-1 divide-y divide-border rounded-xl border border-border bg-foreground/[0.02] md:grid-cols-2 md:divide-y-0 md:divide-x">
                {course.what_you_will_learn && (
                  <div className="p-6">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-primary mb-3">Qué aprenderás</h3>
                    <ul className="space-y-2">
                      {course.what_you_will_learn.split('\n').filter(Boolean).map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                          <Check className="h-4 w-4 shrink-0 mt-0.5 text-primary" aria-hidden="true" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {course.for_whom && (
                  <div className="p-6">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-primary mb-3">Para quién es</h3>
                    <ul className="space-y-2">
                      {course.for_whom.split('\n').filter(Boolean).map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                          <ArrowRight className="h-4 w-4 shrink-0 mt-0.5 text-primary" aria-hidden="true" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Course content — plain module list (no accordions) or flat list */}
            {course.sections && course.sections.length > 0 ? (
              <div className="space-y-2 mt-6">
                {course.sections.map((section, sIdx) => (
                  <SectionAccordion
                    key={section.id}
                    sectionIndex={sIdx + 1}
                    section={section}
                    canAccessLesson={canAccessLesson}
                    completedLessonIds={completedLessonIds}
                    lessonToSlug={lessonToSlug}
                    slug={slug!}
                    navigate={navigate}
                    quizPassed={(quizId) => quizPassedStatus[quizId] === true}
                    onSelectQuiz={(quizId) => setActiveSectionQuizId(quizId)}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {course.lessons.map((lesson, idx) => {
                  const locked = !canAccessLesson(lesson, idx)
                  const isCompleted = completedLessonIds.has(lesson.id)
                  const Icon = iconForResourceType(mainLessonType(lesson))
                  return (
                    <button
                      key={lesson.id}
                      onClick={() => handleLessonClick(lesson, idx)}
                      disabled={locked}
                      className={`group relative flex w-full items-center gap-6 rounded-xl border p-6 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
                        locked
                          ? "border-border bg-foreground/[0.02] cursor-not-allowed opacity-50"
                          : "border-border bg-foreground/[0.02] hover:border-primary/20 hover:bg-foreground/[0.05]"
                      }`}
                    >
                      {/* Indicador de Línea de Tiempo */}
                      <div className="hidden sm:flex flex-col items-center gap-2">
                        <div className={`flex h-12 w-12 items-center justify-center rounded-lg border transition-colors ${
                          isCompleted
                            ? "border-primary/30 bg-primary/10 text-primary"
                            : locked ? "border-foreground/10 bg-foreground/5 text-muted-foreground" : "border-primary/20 bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground"}`}>
                          {isCompleted ? <CheckCircle2 className="h-6 w-6" /> : <Icon className="h-6 w-6" />}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-micro font-semibold uppercase tracking-wide text-muted-foreground">Módulo {String(idx + 1).padStart(2, '0')}</span>
                          {isCompleted && <span className="rounded-full bg-primary/15 px-2 py-0.5 text-micro font-semibold text-primary">Vista</span>}
                          {lesson.is_preview && <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-micro font-semibold text-foreground">Vista previa</span>}
                        </div>
                        <h4 className="text-lg font-semibold tracking-tight text-foreground truncate">{lesson.title}</h4>
                        <p className="mt-1 text-sm text-muted-foreground line-clamp-1">{lesson.description || "Sin descripción disponible."}</p>
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="hidden md:block text-right">
                          <p className="text-xs font-semibold text-muted-foreground">{lesson.duration_minutes} min</p>
                          <p className="text-xs text-primary font-semibold">{labelForResourceType(mainLessonType(lesson))}</p>
                        </div>

                        {locked ? (
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-foreground/5" title={LOCKED_LESSON_REASON}>
                            <Lock className="h-5 w-5 text-muted-foreground" aria-label={LOCKED_LESSON_REASON} />
                          </div>
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-foreground/10 group-hover:bg-primary text-foreground group-hover:text-primary-foreground transition-colors">
                            <ArrowRight className="h-6 w-6" />
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </section>

        <section className="pb-24">
          <div className="container mx-auto max-w-5xl px-6 space-y-16">
            <InstructorSection instructors={course.instructors} />
            <RelatedLearningPathsSection programs={course.programs} />
          </div>
        </section>

        <Footer />
        </main>

        {renderSidebarRight()}
      </div>
    </div>
  )
}
